/* eslint-disable @typescript-eslint/naming-convention */ // Base API fields are snake_case
import { useEffect, useMemo, useRef } from 'react';
import { useAtom } from 'ximple';
import { NativeReserveCalculateRequest } from '@repo/types';
import {
    getProjectsFromCart,
    getToursFromCart,
    nativeProjectLineKey,
    nativeTourLineKey,
} from '@repo/widget-utils/ProductMapper';
import { nativeReserveCalculate } from '@repo/widget-utils/services/fetchers/native-membership';
import { useMembershipMode } from '@repo/widget-utils/widgetsConfiguration';
import {
    createInitialCheckoutInfoData,
    createInitialCompletedPaymentInfo,
} from '@repo/widget-utils/checkout-info-helper';
import { tryFindPaymentRedirectContext } from '@repo/widget-utils/payment-redirect-helper';
import { cartAtom } from 'src/state/cart/cartAtom';
import { loadPurchase } from 'src/state/payment/purchase.localstorage';
import {
    clearCommittedCoverage,
    clearLiveCoverage,
    nativeMemberCoverageAtom,
    resetMemberCardCoverage,
    restoreCommittedCoverageFromSnapshot,
} from 'src/state/native-membership/nativeMemberCoverageAtom';
import { nativeMemberTokenAtom } from 'src/state/native-membership/nativeMemberTokenAtom';
import { voucherAtom } from 'src/state/voucher/voucherAtom';

const DEBOUNCE_MS = 400;

/**
 * App-level native member-card coverage. Mounted once in StateEffects (cart-driven, like
 * usePaymentPlanEffects), so the member's discounted price is available everywhere the cart shows —
 * the booking flow, the cart modal/basket, and the checkout — not just inside the checkout. Writes the
 * shared nativeMemberCoverageAtom that the summary, selection panel, and usePayment read.
 *
 * A no-op for guest/legacy tenants (no token / not native → request is null, no atom writes).
 */
export function useNativeMemberCoverageEffects(): void {
    const isNative = useMembershipMode() === 'native';
    const [token] = useAtom(nativeMemberTokenAtom);
    const [cart] = useAtom(cartAtom);
    const [voucher] = useAtom(voucherAtom);
    const [coverage] = useAtom(nativeMemberCoverageAtom);

    const active = isNative && Boolean(token);

    // A payment-gateway redirect that COMPLETED a purchase: we're rendering a frozen receipt, not an
    // editable cart. Computed purely from the same inputs CartCheckoutContext uses, so this works
    // app-level without the checkout's React state. (A cancel-return resolves to null → live resumes.)
    const redirectReturnCompleted = useMemo(
        () =>
            createInitialCompletedPaymentInfo(loadPurchase(), tryFindPaymentRedirectContext()) !==
            null,
        [],
    );

    const request = useMemo<NativeReserveCalculateRequest | null>(() => {
        if (!active) return null;

        // getToursFromCart needs a shaped CheckoutInfo (it reads the order/guest questionnaires), but
        // the calculate body keeps only id/key/pax — none of which depend on contact info. So we build
        // it from a placeholder rather than subscribing to checkoutInfoAtom; the body is identical and
        // it works app-wide (e.g. the cart modal before any checkout-info exists).
        const ci = createInitialCheckoutInfoData(null, cart, null);
        if (!ci) return null;

        const tours = getToursFromCart(cart, ci).map((tour) => ({
            id: tour.id,
            key: nativeTourLineKey(tour),
            pax: tour.pax.map((p) => ({ id: p.id, quantity: p.quantity })),
        }));
        const projects = getProjectsFromCart(cart).map((project) => ({
            id: project.id,
            key: nativeProjectLineKey(project),
            timeslots: project.timeslots,
            tickets: project.tickets,
        }));

        if (tours.length === 0 && projects.length === 0) return null;

        return {
            tours: tours.length > 0 ? tours : undefined,
            projects: projects.length > 0 ? projects : undefined,
            promo_code: voucher.appliedPromoCode?.coupon_code ?? null,
            giftcard_references: voucher.giftcards.map((g) => g.id),
            member_card_line_overrides: coverage.lineOverrides,
            member_card_excluded_line_keys: coverage.excludedLineKeys,
            member_card_line_groups: coverage.lineGroups,
        };
        // retryNonce is read only to bust this memo after a failed calculate (retryMemberCardCoverage
        // bumps it), so it's an intentional dep the linter can't see used.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        active,
        cart,
        voucher.appliedPromoCode,
        voucher.giftcards,
        coverage.lineOverrides,
        coverage.excludedLineKeys,
        coverage.lineGroups,
        coverage.retryNonce,
    ]);

    // Only re-fetch when the request *body* actually changes — a new cart/voucher reference that
    // serializes the same (e.g. an unrelated field changed) must not re-fire /calculate.
    const requestKey = useMemo(() => (request ? JSON.stringify(request) : null), [request]);
    const cartEmpty = requestKey === null;

    // A fresh shopping session (cart empty → non-empty) drops the previous booking's committed snapshot
    // so it can't flash into the new cart. Skipped on a redirect-return, which needs the snapshot to
    // restore the receipt (and where the cart is present on reload).
    const prevCartEmptyRef = useRef(true);
    useEffect(() => {
        const wasEmpty = prevCartEmptyRef.current;
        prevCartEmptyRef.current = cartEmpty;
        if (active && wasEmpty && !cartEmpty && !redirectReturnCompleted) {
            clearCommittedCoverage();
        }
    }, [active, cartEmpty, redirectReturnCompleted]);

    // On a redirect-return confirmation, restore the committed snapshot (the in-memory copy was lost on
    // reload) so the summary/receipt shows the member-card coverage + actual total, not the gross.
    useEffect(() => {
        if (redirectReturnCompleted) restoreCommittedCoverageFromSnapshot();
    }, [redirectReturnCompleted]);

    useEffect(() => {
        // Freeze the preview when there's nothing to price, or on a redirect-return confirmation (a
        // post-reserve recalc would consume punches and flash a wrong total). The committed snapshot
        // drives the receipt instead. clearLiveCoverage keeps committedResult intact.
        if (!requestKey || !token || redirectReturnCompleted) {
            clearLiveCoverage();
            return;
        }

        const req = JSON.parse(requestKey) as NativeReserveCalculateRequest;
        let cancelled = false;
        nativeMemberCoverageAtom.update({
            ...nativeMemberCoverageAtom.subject.value,
            isCalculating: true,
            error: false,
        });

        const handle = setTimeout(async () => {
            try {
                const result = await nativeReserveCalculate(req, token);
                if (!cancelled) {
                    nativeMemberCoverageAtom.update({
                        ...nativeMemberCoverageAtom.subject.value,
                        result,
                        isCalculating: false,
                        error: false,
                    });
                }
            } catch {
                // Coverage unavailable: flag the error so the panel surfaces a retry and the pay button
                // (gated on isCalculating + error) stays blocked, instead of charging the gross total.
                if (!cancelled) {
                    nativeMemberCoverageAtom.update({
                        ...nativeMemberCoverageAtom.subject.value,
                        result: null,
                        isCalculating: false,
                        error: true,
                    });
                }
            }
        }, DEBOUNCE_MS);

        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [requestKey, token, redirectReturnCompleted]);

    // Full reset (clears selections + committed) when the last widget unmounts.
    useEffect(() => () => resetMemberCardCoverage(), []);
}
